import { UserRound, Baby, User2, Check, ArrowRight, X } from "lucide-react";
import { multigenHousehold, multigenROI } from "@/lib/proposalData";

const iconMap = {
  "user-round": UserRound,
  baby: Baby,
  "user-2": User2,
};

export default function MultigenerationalStory() {
  return (
    <section
      id="story"
      data-testid="section-multigen-story"
      className="bg-[#0A192F] text-[#F9F7F1] py-24 lg:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#D4AF37]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                One Intake · One Day · Three Generations
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl leading-[1.05]">
              The Hernandez household.<br />
              <em className="italic text-[#D4AF37]">90 minutes that change three lives.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#CBD5E1] leading-relaxed">
            <p>{multigenHousehold.setup}</p>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#94A3B8]">
              Composite story · Illustrative of typical Bridge intake outcomes
            </p>
          </div>
        </div>

        {/* 3 generations grid */}
        <div className="grid md:grid-cols-3 gap-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15 mb-14">
          {multigenHousehold.members.map((m, i) => {
            const Icon = iconMap[m.icon] || UserRound;
            return (
              <article
                key={m.name}
                data-testid={`multigen-member-${i}`}
                className="bg-[#0A192F] p-6 lg:p-8 flex flex-col"
              >
                {/* Avatar + role */}
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="grid place-items-center w-14 h-14 border"
                    style={{ borderColor: m.accent, color: m.accent }}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <span
                    className="font-mono text-[10px] uppercase tracking-widest"
                    style={{ color: m.accent }}
                  >
                    {m.role}
                  </span>
                </div>

                <h3 className="font-display text-2xl text-[#F9F7F1]">{m.name}</h3>
                <p className="mt-3 text-sm text-[#CBD5E1] leading-relaxed border-l border-[#F9F7F1]/20 pl-4">
                  {m.situation}
                </p>

                <div className="mt-6 pt-5 border-t border-[#F9F7F1]/15 flex-1">
                  <div
                    className="font-mono text-[10px] uppercase tracking-widest mb-3"
                    style={{ color: m.accent }}
                  >
                    Activated on intake day
                  </div>
                  <ul className="space-y-2.5">
                    {m.activations.map((a) => (
                      <li
                        key={a}
                        className="flex items-start gap-2.5 text-sm text-[#F9F7F1] leading-snug"
                      >
                        <Check
                          className="h-4 w-4 shrink-0 mt-0.5"
                          style={{ color: m.accent }}
                        />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>

        {/* ROI comparison */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
              The ROI Math
            </span>
          </div>
          <h3 className="font-display text-2xl lg:text-4xl leading-tight mb-2">
            One connector. <em className="italic text-[#D4AF37]">Same household. Half the cost.</em>
          </h3>
          <p className="text-[#CBD5E1] max-w-3xl mb-8">
            The traditional safety net would route Maria, Diego, and Rosa to three
            separate agencies, three separate intakes, and three separate case
            workers. Bridge does it on one couch in 90 minutes.
          </p>

          <div className="grid md:grid-cols-2 gap-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15">
            <div data-testid="roi-siloed" className="bg-[#0A192F] p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-5">
                <span className="grid place-items-center w-9 h-9 bg-[#475569]">
                  <X className="h-4 w-4 text-[#F9F7F1]" />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
                    The default
                  </div>
                  <div className="font-display text-xl text-[#F9F7F1]">
                    {multigenROI.siloed.label}
                  </div>
                </div>
              </div>
              <ul className="divide-y divide-[#F9F7F1]/15">
                {multigenROI.siloed.rows.map((r) => (
                  <li key={r.metric} className="py-3 flex items-baseline justify-between gap-4">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
                      {r.metric}
                    </span>
                    <span className="text-sm text-[#CBD5E1] text-right">{r.value}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              data-testid="roi-bridge"
              className="bg-[#15243F] p-6 lg:p-8 border-l-2 border-[#D4AF37]"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="grid place-items-center w-9 h-9 bg-[#8B0000]">
                  <Check className="h-4 w-4 text-[#F9F7F1]" />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">
                    Bridge approach
                  </div>
                  <div className="font-display text-xl text-[#F9F7F1]">
                    {multigenROI.bridge.label}
                  </div>
                </div>
              </div>
              <ul className="divide-y divide-[#F9F7F1]/15">
                {multigenROI.bridge.rows.map((r) => (
                  <li key={r.metric} className="py-3 flex items-baseline justify-between gap-4">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">
                      {r.metric}
                    </span>
                    <span className="text-sm text-[#F9F7F1] text-right font-medium">{r.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Transition line to senior section */}
        <div className="border-t border-[#F9F7F1]/20 pt-8 flex flex-col md:flex-row gap-4 md:items-end md:justify-between">
          <p className="font-display text-xl lg:text-2xl text-[#F9F7F1] leading-snug max-w-3xl">
            That's the Trade Pipeline activating for Maria.<br />
            <em className="italic text-[#D4AF37]">
              Next: the Senior Care Pipeline activating for Rosa — same connector, same day.
            </em>
          </p>
          <a
            href="#seniors"
            data-testid="story-cta-senior"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#D4AF37] hover:text-[#F9F7F1] whitespace-nowrap"
          >
            Continue to Rosa's track <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
